import { Component, Input, ViewChild, type SimpleChanges, type OnChanges, inject } from "@angular/core"
import { MatDialog } from "@angular/material/dialog"
import { MatPaginator } from "@angular/material/paginator"
import { MatTableDataSource } from "@angular/material/table"
import { FilterModalComponent } from "../filter-modal/filter-modal.component"

interface ColumnFilter {
	[column: string]: Set<any>
}

@Component({
	selector: "app-general-table",
	templateUrl: "./general-table.component.html",
	styleUrls: ["./general-table.component.scss"],
})
export class GeneralTableComponent implements OnChanges {
	@Input() info: any[] | null = null
	@Input() headersMap: { [key: string]: string } = {}
	@Input() headersDescription: { [key: string]: string } = {}

	displayedColumns: string[] = []
	dataSource = new MatTableDataSource<any>()

	columnFilters: ColumnFilter = {}
	originalData: any[] = []

	@ViewChild(MatPaginator) paginator!: MatPaginator
	private dialog = inject(MatDialog);

	ngOnChanges(changes: SimpleChanges) {
		if (changes["info"] && this.info) {
			this.originalData = [...this.info]
			this.dataSource.data = this.info
			this.displayedColumns = this.headers
			this.initializeColumnFilters()

			this.dataSource.filterPredicate = this.createFilterPredicate()
			setTimeout(() => {
				this.dataSource.paginator = this.paginator
			})
		}
	}

	get headers(): string[] {
		return this.info?.length ? Object.keys(this.info[0]) : [];
	}

	initializeColumnFilters() {
		this.columnFilters = {}
		this.headers.forEach((header) => {
			this.columnFilters[header] = new Set()
		})
	}

	openFilterMenu(column: string) {
		const dialogRef = this.dialog.open(FilterModalComponent, {
			width: "500px",
			data: {
				column,
				headerLabel: this.getHeaderLabel(column),
				values: this.getUniqueValues(column),
				selectedValues: Array.from(this.columnFilters[column] || []),
			},
		});

		dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				this.columnFilters[column] = new Set(result);
				this.applyColumnFilters();
			}
		});
	}

	getUniqueValues(column: string): any[] {
		if (!this.originalData || !column) return []

		const values = this.originalData
			.map((row) => row[column])
			.filter((value, index, self) => self.indexOf(value) === index)
			.sort()

		return values
	}

	hasActiveFilter(column: string): boolean {
		return this.columnFilters[column]?.size > 0
	}

	createFilterPredicate() {
		return (data: any, filter: string): boolean => {
			if (!filter) return true

			let columnFilters: { [key: string]: any[] }
			try {
				columnFilters = JSON.parse(filter)
			} catch {
				return true
			}

			for (const column in columnFilters) {
				const selectedValues = columnFilters[column]
				if (selectedValues.length > 0 && !selectedValues.includes(data[column])) {
					return false
				}
			}

			return true
		}
	}

	applyColumnFilters() {
		this.dataSource.filter = this.serializeColumnFilters()
		this.dataSource.paginator?.firstPage()
	}

	applyFilter(event: Event) {
		const filterValue = (event.target as HTMLInputElement).value.trim().toLowerCase()

		if (filterValue) this.initializeColumnFilters()

		this.dataSource.filter = filterValue

		if (this.dataSource.paginator) this.dataSource.paginator.firstPage()
	}

	getHeaderDescription(header: string): string {
		return this.headersDescription[header] || ""
	}

	getHeaderLabel(header: string): string {
		return this.headersMap[header] || header
	}

	private serializeColumnFilters(): string {
		const serializable: { [key: string]: any[] } = {}

		for (const column in this.columnFilters) {
			serializable[column] = Array.from(this.columnFilters[column])
		}

		return JSON.stringify(serializable)
	}
}